import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Breadcrumb,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  message,
  TableProps,
} from 'antd';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import SaleTypeModel from './SaleTypeModel';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

interface SaleType {
  st_id: number;
  st_name: string;
  st_status: number;
}

const SaleType: React.FC = () => {
  const [saleTypes, setSaleTypes] = useState<SaleType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<SaleType | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const cancel: PopconfirmProps['onCancel'] = (e) => {
    showError('Action cancelled');
  };

  const fetchSaleTypes = async () => {
    try {
      const data = await apiRequest<SaleType[]>({
        method: 'get',
        url: 'sale-types', // Replace with your actual endpoint
      });
      setSaleTypes(data);
    } catch (error) {
      showError('Failed to fetch sale types');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchSaleTypes();
  }, []);

  const handleEditClick = (record: SaleType) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleCreate = () => {
    fetchSaleTypes();
  };

  const handleDelete = async (st_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-sale-type/${st_id}`, // Adjust API path accordingly
      });
      showSuccess('Sale type deleted');
      fetchSaleTypes();
    } catch {
      showError('Failed to delete sale type');
    }
  };

  const handleStatusToggle = async (st_id: number) => {
    // Optimistic UI update
    setSaleTypes((prev) =>
      prev.map((item) =>
        item.st_id === st_id ? { ...item, st_status: item.st_status === 1 ? 0 : 1 } : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-sale-type/${st_id}`, // Adjust API path accordingly
      });
      showSuccess('Sale type status updated');
    } catch (error) {
      showError('Failed to update status');
      // Revert UI update
      setSaleTypes((prev) =>
        prev.map((item) =>
          item.st_id === st_id ? { ...item, st_status: item.st_status === 1 ? 0 : 1 } : item
        )
      );
    }
  };

  const handleTableChange: TableProps<SaleType>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<SaleType> = [
    {
      title: 'Sale Type',
      dataIndex: 'st_name',
      key: 'st_name',
      filters: Array.from(new Set(saleTypes.map((s) => s.st_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.st_name || null,
      onFilter: (value, record) => record.st_name.includes(value as string),
      sorter: (a, b) => a.st_name.localeCompare(b.st_name),
      sortOrder: sortedInfo.columnKey === 'st_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.st_name}</div>,
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      key: 'actions',
      render: (_: any, record: SaleType) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.st_status}
                onChange={() => handleStatusToggle(record.st_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this sale type?"
              onConfirm={() => handleDelete(record.st_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Sale Types</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Button type="primary" onClick={showCreateModal}>
            Add Sale Type
          </Button>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<SaleType>
          tableLayout="auto"
          columns={columns}
          onChange={handleTableChange}
          rowKey="st_id"
          dataSource={saleTypes.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No sale types found' }}
          footer={() => (
            <Space
              style={{ width: '100%', justifyContent: 'space-between', padding: '0 16px' }}
            >
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, saleTypes.length)} of{' '}
                {saleTypes.length}
              </span>
              <Pagination
                {...pagination}
                total={saleTypes.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Space>
          )}
        />
      </div>

      <SaleTypeModel
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default SaleType;
